import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { RootState } from '@/store/store'
import {
    FiShield,
    FiLock,
    FiEye,
    FiEyeOff,
    FiSmartphone,
    FiMail,
    FiKey,
    FiTrash2,
    FiEdit3,
    FiAlertTriangle,
    FiCheck,
    FiX,
    FiClock
} from 'react-icons/fi'
import { formatDate } from '@/utils/formatters'

interface LoginSession {
    id: number
    device: string
    location: string
    lastActive: Date
    isCurrentSession: boolean
    browser: string
    ipAddress: string
}

interface SecurityEvent {
    id: number
    type: 'login' | 'password_change' | 'email_change' | 'two_factor_enabled' | 'suspicious_activity'
    description: string
    date: Date
    location: string
    status: 'success' | 'failed' | 'blocked'
}

const PrivacySecurityPage: React.FC = () => {
    const { user } = useSelector((state: RootState) => state.auth)
    const [showPassword, setShowPassword] = useState(false)
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
    const [profileVisibility, setProfileVisibility] = useState('public')
    const [emailNotifications, setEmailNotifications] = useState(true)
    const [activeTab, setActiveTab] = useState<'security' | 'privacy' | 'sessions' | 'activity'>('security')

    // Mock data - replace with actual API data
    const loginSessions: LoginSession[] = [
        {
            id: 1,
            device: 'Windows PC',
            location: 'Accra, Ghana',
            lastActive: new Date(),
            isCurrentSession: true,
            browser: 'Chrome 120',
            ipAddress: '192.168.1.100'
        },
        {
            id: 2,
            device: 'iPhone 15',
            location: 'Accra, Ghana',
            lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000),
            isCurrentSession: false,
            browser: 'Safari',
            ipAddress: '192.168.1.101'
        },
        {
            id: 3,
            device: 'MacBook Pro',
            location: 'Kumasi, Ghana',
            lastActive: new Date(Date.now() - 24 * 60 * 60 * 1000),
            isCurrentSession: false,
            browser: 'Chrome 119',
            ipAddress: '10.0.0.15'
        }
    ]

    const securityEvents: SecurityEvent[] = [
        {
            id: 1,
            type: 'login',
            description: 'Successful login from new device',
            date: new Date(Date.now() - 30 * 60 * 1000),
            location: 'Accra, Ghana',
            status: 'success'
        },
        {
            id: 2,
            type: 'password_change',
            description: 'Password changed successfully',
            date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
            location: 'Accra, Ghana',
            status: 'success'
        },
        {
            id: 3,
            type: 'suspicious_activity',
            description: 'Multiple failed login attempts',
            date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
            location: 'Unknown Location',
            status: 'blocked'
        }
    ]

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'success':
                return <FiCheck className="w-4 h-4 text-green-500" />
            case 'failed':
                return <FiX className="w-4 h-4 text-red-500" />
            case 'blocked':
                return <FiShield className="w-4 h-4 text-yellow-500" />
            default:
                return <FiClock className="w-4 h-4 text-gray-500" />
        }
    }

    const getEventTypeIcon = (type: string) => {
        switch (type) {
            case 'login':
                return <FiLock className="w-4 h-4" />
            case 'password_change':
                return <FiKey className="w-4 h-4" />
            case 'email_change':
                return <FiMail className="w-4 h-4" />
            case 'two_factor_enabled':
                return <FiSmartphone className="w-4 h-4" />
            case 'suspicious_activity':
                return <FiAlertTriangle className="w-4 h-4" />
            default:
                return <FiClock className="w-4 h-4" />
        }
    }

    return (
        <>
            <Helmet>
                <title>Privacy & Security - SideGO</title>
                <meta name="description" content="Manage your privacy settings and account security" />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Privacy & Security
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Manage your account security settings and privacy preferences
                    </p>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
                    <nav className="-mb-px flex space-x-8">
                        {[
                            { id: 'security', label: 'Security', icon: FiShield },
                            { id: 'privacy', label: 'Privacy', icon: FiEye },
                            { id: 'sessions', label: 'Active Sessions', icon: FiSmartphone },
                            { id: 'activity', label: 'Security Activity', icon: FiClock }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab Content */}
                {activeTab === 'security' && (
                    <div className="space-y-6">
                        {/* Password Section */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Password
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Current Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            placeholder="Enter current password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                        >
                                            {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            New Password
                                        </label>
                                        <input
                                            type="password"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            placeholder="Enter new password"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Confirm New Password
                                        </label>
                                        <input
                                            type="password"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            placeholder="Confirm new password"
                                        />
                                    </div>
                                </div>
                                <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                                    Update Password
                                </button>
                            </div>
                        </div>

                        {/* Two-Factor Authentication */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Two-Factor Authentication
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Add an extra layer of security to your account
                                    </p>
                                </div>
                                <div className="flex items-center">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={twoFactorEnabled}
                                            onChange={(e) => setTwoFactorEnabled(e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>
                            </div>
                            {twoFactorEnabled && (
                                <div className="space-y-4">
                                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                        <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                                            <FiCheck className="w-5 h-5" />
                                            <span className="font-medium">Two-factor authentication is enabled</span>
                                        </div>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                                            Your account is protected with SMS verification
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                            View Backup Codes
                                        </button>
                                        <button className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                                            Disable 2FA
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Email Security */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Email Security
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {user?.email}
                                        </p>
                                        <p className="text-sm text-green-600 dark:text-green-400">
                                            Verified
                                        </p>
                                    </div>
                                    <button className="flex items-center gap-2 px-3 py-1 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors">
                                        <FiEdit3 className="w-4 h-4" />
                                        Change
                                    </button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            Security Notifications
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Get notified about security events
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={emailNotifications}
                                            onChange={(e) => setEmailNotifications(e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'privacy' && (
                    <div className="space-y-6">
                        {/* Profile Visibility */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Profile Visibility
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Who can see your profile?
                                    </label>
                                    <select
                                        value={profileVisibility}
                                        onChange={(e) => setProfileVisibility(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    >
                                        <option value="public">Everyone (Public)</option>
                                        <option value="registered">Registered Users Only</option>
                                        <option value="private">No One (Private)</option>
                                    </select>
                                </div>
                                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                    <p className="text-sm text-blue-800 dark:text-blue-200">
                                        {profileVisibility === 'public' && 'Your profile is visible to everyone on the platform and search engines.'}
                                        {profileVisibility === 'registered' && 'Only registered users can view your profile.'}
                                        {profileVisibility === 'private' && 'Your profile is hidden from everyone except you.'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Data & Privacy */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Data & Privacy
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            Download Your Data
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Get a copy of all your account data
                                        </p>
                                    </div>
                                    <button className="px-4 py-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors">
                                        Request Data
                                    </button>
                                </div>
                                <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            Delete Account
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Permanently delete your account and all data
                                        </p>
                                    </div>
                                    <button className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                                        Delete Account
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'sessions' && (
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Active Sessions
                                </h3>
                                <button className="text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-1 rounded transition-colors">
                                    Sign out all other sessions
                                </button>
                            </div>
                            <div className="space-y-4">
                                {loginSessions.map((session) => (
                                    <div
                                        key={session.id}
                                        className={`p-4 border rounded-lg ${session.isCurrentSession
                                            ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                                            : 'border-gray-200 dark:border-gray-700'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                                    <FiSmartphone className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 dark:text-white">
                                                        {session.device}
                                                        {session.isCurrentSession && (
                                                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full">
                                                                Current
                                                            </span>
                                                        )}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        {session.browser} • {session.location}
                                                    </p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-500">
                                                        Last active: {formatDate(session.lastActive.toISOString())}
                                                    </p>
                                                </div>
                                            </div>
                                            {!session.isCurrentSession && (
                                                <button className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                                                    <FiTrash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'activity' && (
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                                Recent Security Activity
                            </h3>
                            <div className="space-y-4">
                                {securityEvents.map((event) => (
                                    <div
                                        key={event.id}
                                        className="flex items-start gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                                    >
                                        <div className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                            {getEventTypeIcon(event.type)}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <p className="font-medium text-gray-900 dark:text-white">
                                                    {event.description}
                                                </p>
                                                {getStatusIcon(event.status)}
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {formatDate(event.date.toISOString())} • {event.location}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default PrivacySecurityPage